import { useState } from 'react';
import { useAuthenticateMutation } from '@websky/graphql';
import type { AuthCredentials, AuthenticateMutationHookResult } from '@websky/graphql';
import { PixelMessageBuilder } from '@websky/pixel';
import { sendPixelEvent } from '@shared/lib/pixel/sendPixelEvent';

const sendAuthDetectedPixelEvent = async (authParams: AuthCredentials, externalId: string) => {
	if (!authParams || !externalId) {
		return;
	}

	await sendPixelEvent(cdpId => PixelMessageBuilder.buildAuthDetectedEvent(authParams, cdpId, externalId));
	await new Promise(resolve => setTimeout(resolve, 1000)); // таймаут на отправку события
};

/**
 * Обёртка над useAuthenticateMutation, которая позволяет встроить кастомные действия
 * между получением ответа мутации и возвратом результата.
 *
 * Полностью обратно совместима с исходным API useAuthenticateMutation.
 */
export const useAuthenticate = (): AuthenticateMutationHookResult => {
	const [isLoading, setIsLoading] = useState(false);

	const [originalMutate, otherResults] = useAuthenticateMutation();

	const wrappedMutate: typeof originalMutate = async options => {
		setIsLoading(true);
		try {
			const result = await originalMutate(options);

			if ((!result.errors || result.errors.length === 0) && result.data?.Authenticate) {
				await sendAuthDetectedPixelEvent(options?.variables?.params, result.data.Authenticate.user?.userId);
			}

			return result;
		} catch (error) {
			throw error;
		} finally {
			setIsLoading(false);
		}
	};

	return [wrappedMutate, { ...otherResults, loading: otherResults.loading || isLoading }];
};
